import type { ProfileCompletion, ProfileCategory } from '@swipeopinion/shared';

const CATEGORY_LABELS: Record<ProfileCategory, string> = {
  demographic: 'Demographics',
  psychographic: 'Lifestyle & Interests',
  behavioral: 'Home & Habits',
  household: 'Household',
};

interface Props {
  completion: ProfileCompletion;
}

export default function ProfileStrengthRing({ completion }: Props) {
  const { overall, categories } = completion;

  // A category is flagged "incomplete" only when it is genuinely sparse
  // (below 50%). A category at, say, 86% has nearly everything filled —
  // flagging it as incomplete just because it isn't a perfect 100% reads
  // as noise (2026-05-15 profile audit).
  const INCOMPLETE_THRESHOLD = 50;
  const incompleteCategories = (Object.entries(categories) as [ProfileCategory, number][]).filter(
    ([, pct]) => pct < INCOMPLETE_THRESHOLD,
  );

  const strokeColor = overall >= 80 ? '#10B981' : overall >= 50 ? '#5B4FE8' : '#F97316';

  return (
    <div className="bg-white rounded-2xl shadow-sm p-6">
      <h2 className="text-sm font-bold text-[#111827] uppercase tracking-wide mb-4">Profile Strength</h2>

      <div className="flex items-center gap-5 mb-5">
        <div className="relative w-20 h-20 flex-shrink-0">
          <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
            <circle cx="18" cy="18" r="15.9" fill="none" stroke="#E5E7EB" strokeWidth="3" />
            <circle
              cx="18" cy="18" r="15.9" fill="none"
              stroke={strokeColor} strokeWidth="3"
              strokeDasharray={`${overall} ${100 - overall}`}
              strokeLinecap="round"
            />
          </svg>
          <span className="absolute inset-0 flex items-center justify-center text-lg font-bold text-[#111827]">
            {overall}%
          </span>
        </div>

        <div className="text-sm text-[#6B7280]">
          {overall === 100
            ? 'Your profile is complete!'
            : overall >= 80
            ? 'Almost there — keep going!'
            : overall >= 50
            ? 'Good start — complete more to unlock better surveys'
            : 'Fill in your profile to unlock more surveys'}
          {incompleteCategories.length > 0 && (
            <p className="text-xs text-[#6B7280] mt-1">
              {incompleteCategories.length} categor{incompleteCategories.length === 1 ? 'y' : 'ies'} incomplete
            </p>
          )}
        </div>
      </div>

      <div className="space-y-2.5">
        {(Object.entries(categories) as [ProfileCategory, number][]).map(([cat, pct]) => (
          <div key={cat}>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-[#6B7280] font-medium">{CATEGORY_LABELS[cat]}</span>
              <span className={pct === 100 ? 'text-[#10B981] font-medium' : 'text-[#6B7280]'}>{pct}%</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-1.5">
              <div
                className={`h-1.5 rounded-full transition-all ${pct === 100 ? 'bg-[#10B981]' : 'bg-[#5B4FE8]'}`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
